import { Component } from "@theme/component";

/**
 * Announcement bar web component with Swiper integration
 */
class AnnouncementBar extends Component {
  /**
   * @type {import('swiper').Swiper | null}
   */
  #swiper = null;

  /**
   * Bound version of handleTimerComplete for event listener removal
   * @type {Function | null}
   */
  #boundHandleTimerComplete = null;

  connectedCallback() {
    super.connectedCallback();

    const completedTimer = this.querySelector("countdown-timer[data-completed]");
    const timerSlide = this.querySelector("[data-slide-type='timer']");
    if (completedTimer && timerSlide instanceof HTMLElement) {
      timerSlide.style.display = "none";
    }

    this.#initSwiper();

    // Listen for countdown completion to re-initialize swiper
    // This handles cases where a timer slide expires and is hidden via CSS
    this.#boundHandleTimerComplete = this.#handleTimerComplete.bind(this);
    this.addEventListener("countdown-complete", this.#boundHandleTimerComplete);

    // console.log("Announcement bar component registered and connected");
  }

  disconnectedCallback() {
    if (this.#swiper) {
      this.#swiper.destroy(true, false);
      this.#swiper = null;
    }
    if (this.#boundHandleTimerComplete) {
      this.removeEventListener("countdown-complete", this.#boundHandleTimerComplete);
    }
  }

  /**
   * Hide timer slide and re-initialize Swiper when a timer expires
   * @param {CustomEvent} event
   */
  #handleTimerComplete(event) {
    const timer = event?.detail?.timer;
    const timerSlide = timer instanceof HTMLElement ? timer.closest("[data-slide-type='timer']") : null;

    // Destroy swiper first — use cleanStyles:false to preserve container inline
    // background/color; timer slide is hidden after destroy.
    if (this.#swiper) {
      this.#swiper.destroy(true, false);
      this.#swiper = null;
    }

    if (timerSlide instanceof HTMLElement) {
      timerSlide.style.display = "none";
    }

    this.#initSwiper();
  }

  /**
   * Initialize Swiper for announcement slides
   */
  #initSwiper() {
    const promoBarSlider = this.querySelector(".announcement-swiper");

    if (!promoBarSlider) {
      console.warn("No swiper container found in announcement bar");
      return;
    }

    // Wait for Swiper to be available
    if (typeof Swiper === "undefined") {
      console.warn("Swiper not loaded yet, retrying...");
      setTimeout(() => this.#initSwiper(), 100);
      return;
    }

    // Before counting, we need to handle slides that are hidden
    // Swiper's loop mode breaks if slides have display: none but still have the swiper-slide class
    const allPotentialSlides = Array.from(this.querySelectorAll("[data-slide-type='timer'], [data-block-id]"));

    allPotentialSlides.forEach(slide => {
      const completedTimer = slide.querySelector('countdown-timer[data-completed="true"]');
      if (completedTimer && slide.getAttribute("data-slide-type") === "timer") {
        slide.style.display = "none";
      }

      const style = window.getComputedStyle(slide);
      const isHidden = style.display === "none" || slide.hasAttribute("hidden");
      if (isHidden) {
        slide.classList.remove("swiper-slide");
        slide.classList.add("swiper-slide-hidden");
      } else {
        slide.classList.add("swiper-slide");
        slide.classList.remove("swiper-slide-hidden");
      }
    });

    // Now count only the ones that have the swiper-slide class
    const promoSlides = Array.from(promoBarSlider.querySelectorAll(".swiper-slide"));
    const slideCount = promoSlides.length;

    // If no slides are visible, hide navigation and exit
    const navLeft = this.querySelector(".announcement__left");
    const navRight = this.querySelector(".announcement__right");

    if (slideCount === 0) {
      if (navLeft instanceof HTMLElement) navLeft.style.display = "none";
      if (navRight instanceof HTMLElement) navRight.style.display = "none";
      return;
    }

    // Hide navigation if only one slide is visible
    if (slideCount <= 1) {
      if (navLeft instanceof HTMLElement) navLeft.style.display = "none";
      if (navRight instanceof HTMLElement) navRight.style.display = "none";
    } else {
      if (navLeft instanceof HTMLElement) navLeft.style.display = "";
      if (navRight instanceof HTMLElement) navRight.style.display = "";
    }

    const slidesPerView = 1;

    // Swiper loop requires at least slidesPerView * 2 slides to work properly
    // Also disable loop if we only have 1 visible slide
    const shouldLoop = slideCount > 1 && slideCount >= slidesPerView * 2;

    if (promoBarSlider) {
      // @ts-ignore
      const promoSwiper = new Swiper(promoBarSlider, {
        slidesPerView: slidesPerView,
        loop: shouldLoop,
        enabled: slideCount > 1,
        spaceBetween: 0,
        autoplay:
          slideCount > 1
            ? {
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false,
        navigation: {
          nextEl: ".announcement__right",
          prevEl: ".announcement__left",
        },
        on: {
          slideChange: function () {
            // Update aria-hidden and tabindex for all slides
            // @ts-ignore
            const slides = this.slides;
            // @ts-ignore
            const realIndex = this.realIndex; // Get real index in loop mode

            let i = 0; // We still need an index counter
            for (const slide of slides) {
              // Check if this is the currently active slide
              const isActive =
                // @ts-ignore
                i === this.activeIndex || (this.isLoop && (i - this.loopedSlides) % slideCount === realIndex);

              if (isActive) {
                slide.setAttribute("aria-hidden", "false");
                const links = slide.querySelectorAll("a");
                links.forEach(link => link.removeAttribute("tabindex"));
              } else {
                slide.setAttribute("aria-hidden", "true");
                const links = slide.querySelectorAll("a");
                links.forEach(link => link.setAttribute("tabindex", "-1"));
              }

              i++; // Increment the counter for each iteration
            }
          },
        },
      });

      this.#swiper = promoSwiper;
    }
  }

  /**
   * Get the Swiper instance
   * @returns {import('swiper').Swiper | null}
   */
  get swiper() {
    return this.#swiper;
  }
}

if (!window.customElements.get("announcement-bar")) {
  window.customElements.define("announcement-bar", AnnouncementBar);
}
